import type { CountryInfo, SupportedCountry } from '../types';

export const countriesList: Record<SupportedCountry, CountryInfo> = {
    NZ: {
        shortName: 'NZ',
        name: 'New Zealand',
        nameCasual: 'New Zealand',
        domain: 'archipro.co.nz',
        language: 'en',
        flagEmoji: '🇳🇿',
    },
    AU: {
        shortName: 'AU',
        name: 'Australia',
        nameCasual: 'Australia',
        domain: 'archipro.com.au',
        language: 'en',
        flagEmoji: '🇦🇺',
    },
    US: {
        shortName: 'US',
        name: 'America',
        nameCasual: 'the USA',
        domain: 'archiproamerica.com',
        language: 'en',
        flagEmoji: '🇺🇸',
    },
};

/**
 * Origin market used as the `x-default` hreflang target when a page is
 * available in more than one market. New Zealand is ArchiPro's first market.
 */
export const DEFAULT_MARKET: SupportedCountry = 'NZ';

/**
 * Production apex host for a market, e.g. `archiproamerica.com` (or
 * `https://archiproamerica.com` when `https` is set). This is the single
 * source of truth for SEO canonical/hreflang/alternate hosts.
 */
export const getCountryHost = (
    country: SupportedCountry,
    { https = false }: { https?: boolean } = {},
): string => {
    const domain = countriesList[country].domain;
    return https ? `https://${domain}` : domain;
};

/**
 * hreflang / HTML `lang` value for a market, e.g. `en-us`. Derived from the
 * market language plus its country code so new markets need no code change.
 */
export const getCountryHrefLang = (country: SupportedCountry): string => {
    const { language } = countriesList[country];
    return `${language}-${country.toLowerCase()}`;
};

/**
 * Open Graph `og:locale` value for a market, e.g. `en_US`. Derived from the
 * market language plus its country code.
 */
export const getCountryOgLocale = (country: SupportedCountry): string => {
    const { language } = countriesList[country];
    return `${language}_${country.toUpperCase()}`;
};

/**
 * Replaces the full country domain in a hostname.
 * Handles production domains (archipro.co.nz, archiproamerica.com)
 * and prefixed environments (dev.archipro.co.nz, bc-1234.dev.archiproamerica.com).
 */
export const swapDomain = (
    host: string,
    fromCountry: SupportedCountry,
    toCountry: SupportedCountry,
): string => {
    const fromDomain = countriesList[fromCountry].domain;
    const toDomain = countriesList[toCountry].domain;
    return host.replace(fromDomain, toDomain);
};
